// app/frontend/components/inbox/ConversationHeader.tsx

import { useState, useRef, useEffect } from 'react'
import { Check, CircleDashed, Clock, Pencil, MoreHorizontal, Archive, Trash2, AlertTriangle, Mail, PanelRight, UserCheck, UserPlus, Send, Star } from 'lucide-react'
import { CopyableText } from '@/components/ui/CopyableText'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { VisitorAvatar } from '@/components/ui/VisitorAvatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { cn } from '@/lib/utils'
import type { Visitor } from '@/types/conversation'

interface TeamMember {
  id: string
  name: string
}

interface ConversationHeaderProps {
  visitor: Visitor
  status: 'open' | 'closed'
  subject?: string | null
  displaySubject?: string | null
  assignee?: { id: string; name: string } | null
  csatResponse?: { rating: number; comment: string | null; responded_at: string } | null
  teamMembers?: TeamMember[]
  currentUserId: string
  onClose: () => void
  onReopen: () => void
  onUpdateSubject: (subject: string) => void
  onArchive: () => void
  onMarkAsSpam: () => void
  onMarkAsUnread: () => void
  onDelete: () => void
  onAssign: (userId: string | null) => void
  onSendTranscript: (email: string) => void
  onToggleVisitorDetails?: () => void
}

export function ConversationHeader({
  visitor,
  status,
  subject,
  displaySubject,
  assignee,
  csatResponse,
  teamMembers = [],
  currentUserId,
  onClose,
  onReopen,
  onUpdateSubject,
  onArchive,
  onMarkAsSpam,
  onMarkAsUnread,
  onDelete,
  onAssign,
  onSendTranscript,
  onToggleVisitorDetails
}: ConversationHeaderProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(subject || '')
  const [transcriptDialogOpen, setTranscriptDialogOpen] = useState(false)
  const [transcriptEmail, setTranscriptEmail] = useState(visitor.email || '')
  const inputRef = useRef<HTMLInputElement>(null)

  const handleTranscriptSubmit = () => {
    const trimmed = transcriptEmail.trim()
    if (!trimmed) return
    onSendTranscript(trimmed)
    setTranscriptDialogOpen(false)
  }

  const openTranscriptDialog = () => {
    setTranscriptEmail(visitor.email || '')
    setTranscriptDialogOpen(true)
  }

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleStartEdit = () => {
    setEditValue(subject || displaySubject || '')
    setIsEditing(true)
  }

  const handleSave = () => {
    const trimmed = editValue.trim()
    if (trimmed && trimmed !== subject) {
      onUpdateSubject(trimmed)
    }
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      setIsEditing(false)
      setEditValue(subject || '')
    }
  }

  const shownSubject = displaySubject || 'No subject'
  const isAutoGenerated = !subject && displaySubject
  const canAssignToSelf = assignee?.id !== currentUserId

  return (
    <div className="border-b border-border bg-card px-4 py-3 shrink-0">
      {/* Subject line */}
      <div className="mb-2">
        {isEditing ? (
          <Input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            placeholder="Enter a subject..."
            className="text-sm font-medium h-7 py-1"
          />
        ) : (
          <button
            onClick={handleStartEdit}
            className="group flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-primary transition-colors cursor-pointer"
          >
            <span className={isAutoGenerated ? 'italic text-muted-foreground' : ''}>
              {shownSubject}
            </span>
            <Pencil className="size-3 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <VisitorAvatar
            name={visitor.name}
            identified={visitor.identified}
            active={visitor.active}
            countryCode={visitor.country_code}
            size="lg"
          />
          <div>
            <h2 className="font-medium">{visitor.name}</h2>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {visitor.email && <CopyableText text={visitor.email} toastMessage="Email copied to clipboard" />}
              {visitor.last_seen_at && (
                <span className="flex items-center gap-1">
                  <Clock className="size-3" />
                  Last seen {new Date(visitor.last_seen_at).toLocaleString()}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* One-click "Assign to me" — only when not already mine */}
          {canAssignToSelf && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onAssign(currentUserId)}
              className="cursor-pointer gap-1.5"
              title="Assign this conversation to me"
            >
              <UserPlus className="size-4" />
              <span className="text-xs">Assign to me</span>
            </Button>
          )}

          {/* CSAT rating badge */}
          {csatResponse && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2.5 py-1 text-xs font-medium text-warning"
              title={
                `Visitor rated ${csatResponse.rating}/5` +
                (csatResponse.comment ? ` — “${csatResponse.comment}”` : '')
              }
            >
              <Star className="size-3.5 fill-current" />
              {csatResponse.rating}/5
            </span>
          )}

          {/* Assign dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="cursor-pointer gap-1.5" title="Assign conversation">
                <UserCheck className="size-4" />
                <span className="text-xs max-w-[100px] truncate">
                  {assignee ? assignee.name : 'Unassigned'}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canAssignToSelf && (
                <>
                  <DropdownMenuItem onClick={() => onAssign(currentUserId)} className="cursor-pointer">
                    Assign to me
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem
                onClick={() => onAssign(null)}
                className={cn('cursor-pointer', !assignee && 'bg-accent text-accent-foreground')}
              >
                Unassigned
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {teamMembers.map((member) => (
                <DropdownMenuItem
                  key={member.id}
                  onClick={() => onAssign(member.id)}
                  className={cn('cursor-pointer', assignee?.id === member.id && 'bg-accent text-accent-foreground')}
                >
                  {member.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {status === 'open' ? (
            <Button
              size="sm"
              onClick={onClose}
              className="cursor-pointer bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              <CircleDashed className="size-4 mr-2" />
              Unresolved
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={onReopen}
              className="cursor-pointer bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Check className="size-4 mr-2" />
              Resolved
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="cursor-pointer size-8 p-0">
                <MoreHorizontal className="size-4" />
                <span className="sr-only">More actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onMarkAsUnread} className="cursor-pointer">
                <Mail className="size-4 mr-2" />
                Mark as unread
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onArchive} className="cursor-pointer">
                <Archive className="size-4 mr-2" />
                Archive
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onMarkAsSpam} className="cursor-pointer">
                <AlertTriangle className="size-4 mr-2" />
                Mark as spam
              </DropdownMenuItem>
              <DropdownMenuItem onClick={openTranscriptDialog} className="cursor-pointer">
                <Send className="size-4 mr-2" />
                Send transcript
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <ConfirmDialog
                trigger={
                  <DropdownMenuItem
                    onSelect={(e) => e.preventDefault()}
                    className="cursor-pointer text-destructive focus:text-destructive"
                  >
                    <Trash2 className="size-4 mr-2" />
                    Delete conversation
                  </DropdownMenuItem>
                }
                title="Delete conversation?"
                description="This will permanently delete this conversation and all its messages. This action cannot be undone."
                confirmText="Delete"
                destructive
                onConfirm={onDelete}
              />
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Visitor details panel toggle */}
          {onToggleVisitorDetails && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleVisitorDetails}
              className="cursor-pointer"
              title="View visitor details"
            >
              <PanelRight className="size-4" />
              <span className="sr-only">View visitor details</span>
            </Button>
          )}
        </div>
      </div>

      <Dialog open={transcriptDialogOpen} onOpenChange={setTranscriptDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send conversation transcript</DialogTitle>
            <DialogDescription>
              The full conversation will be emailed as an HTML transcript, including any attachments.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label htmlFor="transcript-email" className="text-sm font-medium mb-2 block">
              Send to
            </label>
            <Input
              id="transcript-email"
              type="email"
              value={transcriptEmail}
              onChange={(e) => setTranscriptEmail(e.target.value)}
              placeholder={visitor.email || 'recipient@example.com'}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleTranscriptSubmit()
                }
              }}
              autoFocus
            />
            {visitor.email && transcriptEmail !== visitor.email && (
              <p className="text-xs text-muted-foreground mt-1.5">
                Visitor's email is{' '}
                <button
                  type="button"
                  className="underline cursor-pointer"
                  onClick={() => setTranscriptEmail(visitor.email!)}
                >
                  {visitor.email}
                </button>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTranscriptDialogOpen(false)} className="cursor-pointer">
              Cancel
            </Button>
            <Button onClick={handleTranscriptSubmit} disabled={!transcriptEmail.trim()} className="cursor-pointer">
              <Send className="size-4 mr-2" />
              Send transcript
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ConversationHeader
